import { useState, useEffect, useMemo } from 'react'
import { ShipmentIssueTypeCategory } from '../utils/filterIssueTypes'

interface IUsePagination {
  pageSize: number
  currentPage: number
  visibleIssueTypes: ShipmentIssueTypeCategory[]
  setCurrentPage: (newPage: number) => void
  numberOfPages: number
  totalIssues: number
  lowWindowNumber: number
  highWindowNumber: number
}

const PAGE_SIZE = 25

const usePagination = (issueTypes: ShipmentIssueTypeCategory[]): IUsePagination => {
  const [pages, setPages] = useState<ShipmentIssueTypeCategory[][]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const totalIssues = issueTypes.length

  /**
   * This calculates and splits the issues into small arrays that will be considered pages
   * Will recalculate when issueTypes change
   */
  useEffect(() => {
    const slicedPages = []

    for (let i = 0; i < issueTypes.length; i += PAGE_SIZE) {
      slicedPages.push(issueTypes.slice(i, i + PAGE_SIZE))
    }

    setPages(slicedPages)
    setCurrentPage(1)
  }, [issueTypes])

  const lowWindowNumber = useMemo(
    () => (issueTypes.length ? currentPage * PAGE_SIZE - (PAGE_SIZE - 1) : 0),
    [currentPage, issueTypes]
  )
  const highWindowNumber = useMemo(() => Math.min(currentPage * PAGE_SIZE, totalIssues), [
    currentPage,
    totalIssues,
  ])

  return {
    pageSize: PAGE_SIZE,
    currentPage,
    visibleIssueTypes: pages[currentPage - 1] || [],
    setCurrentPage,
    numberOfPages: pages.length,
    totalIssues,
    lowWindowNumber,
    highWindowNumber,
  }
}

export default usePagination
